<template>
  <div
    v-if="!isShowModalBetSelectionInjected && !$device.isMobile"
    class="pusherPlaceBet"
    :class="{
      pusherPlaceBetMobile: $device.isMobile
    }"
  >
    <UIPrizePool
      :title="$t('coinPusherPrizePool')"
      :value="data?.prizePool ? addThousandSeparators(data?.prizePool) : '0'"
      :token="tokenToPlay"
      :is-loading-data="isLoadingTokenConfig"
      @token-change="handleTokenChange"
    />

    <div class="winingChanceWrapper">
      <UIWiningChance :value="winningChanceValue" :title="winingChanceLabel" />
    </div>

    <div class="pusherTicketsRemaining">
      <div v-if="showTimer" class="pusherTimerPlaceBet">
        <div class="whiteTitle">{{ $t('coinPusherTicketLimit') }}:</div>
        <TavernPusherTimer />
      </div>

      <div v-if="pusherGameStatusInjected !== PusherGameStatus.Launched" class="pusherTicketsRemainingContainer">
        <span class="whiteTitle">{{ $t('coinPusherTicketsRemaining') }}:</span>
        <span :class="[data?.remainingBetLimit === 0 ? 'redTitleLarge' : 'orangeTitleLarge']">{{
          data?.remainingBetLimit
        }}</span>
      </div>
    </div>

    <TavernPusherTimerButton v-if="data?.remainingBetLimit === 0" />
    <TavernFlipCoinBigButton
      v-else
      :label="$t('coinPusherPlaceBet')"
      class="button-width"
      :disabled="pusherGameStatusInjected === PusherGameStatus.Launched"
      @click="$emit('click-bet-selection')"
    />
  </div>

  <div v-if="$device.isMobile" class="pusherPlaceBetMobile">
    <div class="pusherPlaceBetMobileTop">
      <UIPrizePool
        :title="$t('coinPusherPrizePool')"
        :value="data?.prizePool ? addThousandSeparators(data?.prizePool) : '0'"
        :token="tokenToPlay"
        @token-change="handleTokenChange"
      />

      <UIWiningChance :value="winningChanceValue" :title="winingChanceLabel" />
    </div>

    <div v-if="pusherGameStatusInjected !== PusherGameStatus.Launched" class="pusherPlaceBetMobileBottom">
      <div class="pusherTicketsRemaining">
        <span class="whiteTitle">{{ $t('coinPusherTicketsRemaining') }}:</span>
        <span :class="[data?.remainingBetLimit === 0 ? 'redTitleLarge' : 'orangeTitleLarge']">{{
          data?.remainingBetLimit
        }}</span>
      </div>

      <div v-if="showTimer" class="pusherTimerPlaceBet">
        <div class="whiteTitle">{{ $t('coinPusherTicketLimit') }}:</div>
        <TavernPusherTimer />
      </div>

      <TavernPusherTimerButton v-if="data?.remainingBetLimit === 0" />
      <TavernFlipCoinBigButton
        v-else
        :label="$t('coinPusherPlaceBet')"
        class="button-width"
        @click="$emit('click-bet-selection')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { Tokens } from '~/utils/constants';
import UIWiningChance from '~/components/UI/UIWiningChance.vue';
import { useI18n } from '#imports';
import { type PusherConfigResponse, PusherGameStatus } from '~/types/Pusher';
import { addThousandSeparators } from '~/utils/formatters';

const { t } = useI18n();
const { data } = useNuxtData<PusherConfigResponse>('pusherConfig');
const tokenToPlay = defineModel<Tokens>('token');
const { isLoadingTokenConfig } = defineProps<{ isLoadingTokenConfig: boolean }>();

const isShowModalBetSelectionInjected = inject<Ref<boolean>>('showModalBetSelection');
const pusherGameStatusInjected = inject<Ref<PusherGameStatus>>('pusherGameStatus');

defineEmits(['click-bet-selection']);

const handleTokenChange = (newToken: Tokens) => {
  tokenToPlay.value = newToken;
};

const winingChanceLabel = computed(() => {
  return `${t('coinPusherWinningChance')}:`;
});

const winningChanceValue = computed(() => {
  if (!data.value?.winChance) {
    return '';
  }
  return `${data.value.winChance}%`;
});

const showTimer = computed(() => data.value && data.value?.remainingBetLimit < 1);
</script>

<style scoped lang="scss">
.pusherPlaceBet {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 434px;
  min-height: 438px;
}
.pusher-timer {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.pusherTicketsRemaining {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 35px;
  margin-bottom: 35px;
  min-height: 137px;

  .pusherTicketsRemainingContainer {
    display: flex;
    gap: 8px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
}

.betSizeContainer {
  display: flex;
  justify-content: center;
  align-items: center;

  .iconValueContainer {
    display: flex;
    align-items: center;
  }
}

.winingChanceWrapper {
  margin-top: 10px;
}

.pusherPlaceBetMobileTop {
  display: flex;
  flex-direction: column;
  height: 135px;
  width: 90%;
  position: absolute;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(3px);
  border: solid 2px rgba(22, 14, 10, 1);
  background-color: rgba(14, 9, 7, 0.5);
  border-radius: 18px;
  transform: translateX(-50%);
  left: 50%;
  top: -90px;
}

.pusherPlaceBetMobileBottom {
  background-color: rgba(14, 9, 7, 0.5);
  backdrop-filter: blur(2px);
  border: solid 1px rgba(22, 14, 10, 1);
  border-radius: 18px;
  width: 90%;
  transform: translateX(-50%);
  left: 50%;
  position: absolute;
  bottom: 80px;
  padding-top: 9px;
  padding-bottom: 4px;

  .button-width {
    min-width: 217px;
  }

  .gradient-button {
    margin-top: 2px;
  }

  .pusherTimerPlaceBet {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 8px;
    text-align: center;
  }
}

.pusherTimerPlaceBet {
  margin-bottom: 16px;
}

.pusherPlaceBetMobile {
  .pusherTicketsRemaining {
    min-height: unset;
    margin-bottom: 4px;
  }
}
</style>

<style lang="scss">
.pusherPlaceBetMobileTop {
  .prizePool {
    gap: 0;
  }

  .prizePoolContainer {
    height: 47px;
    padding: 5px 20px;
  }

  .winingChanceContainer {
    flex-direction: row;
    height: 32px;
    position: absolute;
    bottom: -14px;
  }
}

.pusherPlaceBetMobileBottom {
  .pusherTicketsRemaining {
    flex-direction: row;
    gap: 5px;
    margin: 0;
  }
}

.wrapperContainer > .pusherPlaceBetMobile .pusherTicketsRemaining {
  .orangeTitleLarge,
  .redTitleLarge {
    font-size: 20px;
  }
}

.pusherTimerPlaceBet {
  .el-statistic__number {
    color: var(--orange);
  }
}
</style>
